import { useState } from "react";
import {
	Box,
	Paper,
	Typography,
	Chip,
	Button,
	IconButton,
	Tooltip,
	Stack,
} from "@mui/material";
import {
	AddOutlined,
	DeleteOutline,
	Edit,
	EditOutlined,
	HistoryOutlined,
} from "@mui/icons-material";
import DatagridComponent from "../../../datagridComponent";
import DrawerAutomacao from "../drawerAutomacao";
import ModalExecucoes from "../modalExecucoes";

// Aba "Automações": regras que disparam ação (ticket / API) quando um host
// fica em alerta por N minutos.
export default function AbaAutomacoes({
	automacoes = [],
	tipos = [],
	clientes = [],
	allHosts = [],
	onSalvar,
	onRemover,
	onTestar,
	carregando,
}) {
	const [drawerAberto, setDrawerAberto] = useState(false);
	const [emEdicao, setEmEdicao] = useState(null);
	const [execucoesDe, setExecucoesDe] = useState(null);

	const abrirNova = () => {
		setEmEdicao(null);
		setDrawerAberto(true);
	};
	const abrirEdicao = (row) => {
		setEmEdicao(row);
		setDrawerAberto(true);
	};

	const rotuloEscopo = (r) => {
		if (r.escopo === "GLOBAL") return "Global";
		if (r.escopo === "CLIENTE") {
			const cliente = r.clienteNome || r.clienteId;
			const qtd = r.servicoIds?.length || 0;
			// Sem lista = todos os serviços do cliente (padrão).
			return qtd
				? `Cliente: ${cliente} (${qtd} serviço${qtd > 1 ? "s" : ""})`
				: `Cliente: ${cliente} (todos os serviços)`;
		}
		// /zabbix/hostList devolve `host` (não `name`) — ler `name` mostrava o id cru.
		const host = allHosts.find((h) => String(h?.hostid) === String(r.hostId));
		return `Host: ${host?.host || host?.name || r.hostId}`;
	};

	const colunas = [
		{ field: "nome", headerName: "Nome", flex: 1, minWidth: 160 },
		{
			field: "escopo",
			headerName: "Escopo",
			flex: 1.4,
			minWidth: 260,
			// x-data-grid v7: valueGetter é (value, row), não (params)
			valueGetter: (_, row) => rotuloEscopo(row),
			// O rótulo do escopo é longo (cliente + serviços) e a célula corta com
			// reticências — o texto inteiro fica no tooltip.
			renderCell: (p) => (
				<Tooltip title={p.value || ""}>
					<Typography variant="inherit" noWrap>
						{p.value}
					</Typography>
				</Tooltip>
			),
		},
		{
			field: "tipoAlertaNome",
			headerName: "Tipo",
			width: 130,
			valueGetter: (value) => value || "Qualquer",
		},
		{
			field: "duracaoMinutos",
			headerName: "Após (min)",
			width: 100,
		},
		{
			field: "acao",
			headerName: "Ação",
			width: 130,
			renderCell: (p) => (
				<Chip
					size="small"
					variant="outlined"
					label={p.value === "CRIAR_TICKET" ? "Criar ticket" : "Chamar API"}
				/>
			),
		},
		{
			field: "totalExecucoes",
			headerName: "Execuções",
			width: 100,
			valueGetter: (value) => value ?? 0,
		},
		{
			field: "ativo",
			headerName: "Ativa",
			width: 80,
			renderCell: (p) => (
				<Chip
					size="small"
					color={p.value ? "success" : "default"}
					label={p.value ? "Sim" : "Não"}
				/>
			),
		},
		{
			field: "acoes",
			headerName: "",
			width: 120,
			sortable: false,
			renderCell: (p) => (
				<Stack direction="row">
					<Tooltip title="Ver execuções">
						<IconButton size="small" onClick={() => setExecucoesDe(p.row)}>
							<HistoryOutlined fontSize="small" />
						</IconButton>
					</Tooltip>
					<Tooltip title="Editar">
						<IconButton size="small" onClick={() => abrirEdicao(p.row)}>
							<Edit fontSize="small" />
						</IconButton>
					</Tooltip>
					<Tooltip title="Remover">
						<IconButton size="small" onClick={() => onRemover(p.row.id)}>
							<DeleteOutline fontSize="small" color="error" />
						</IconButton>
					</Tooltip>
				</Stack>
			),
		},
	];

	return (
		<Paper variant="outlined" sx={{ p: 2 }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					mb: 2,
				}}
			>
				<Box>
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
						Automações por tempo
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Dispara uma ação (ticket no ERP ou chamada de API) quando um host
						fica em alerta por N minutos.
					</Typography>
				</Box>
				<Button
					variant="contained"
					size="small"
					startIcon={<AddOutlined />}
					onClick={abrirNova}
				>
					Nova automação
				</Button>
			</Box>

			<Box sx={{ height: 440, width: "100%" }}>
				<DatagridComponent
					rows={automacoes}
					columns={colunas}
					loading={carregando}
					isPagination
					// O datagridComponent trava a linha em 28px (!important no seu
					// styles.js) enquanto a célula fica 35px — o excedente é cortado.
					// Estas linhas têm chip (24px) e botões de ação (30px), então
					// precisam de mais altura; liberamos só nesta instância.
					options={{ getRowHeight: () => 38 }}
					sxDatagrid={{
						"& .MuiDataGrid-row": {
							minHeight: "38px !important",
							maxHeight: "38px !important",
						},
					}}
				/>
			</Box>

			<DrawerAutomacao
				open={drawerAberto}
				onClose={() => setDrawerAberto(false)}
				automacao={emEdicao}
				tipos={tipos}
				clientes={clientes}
				allHosts={allHosts}
				onTestar={onTestar}
				onSalvar={async (payload) => {
					const ok = await onSalvar(payload);
					if (ok !== false) setDrawerAberto(false);
				}}
			/>

			<ModalExecucoes
				open={!!execucoesDe}
				onClose={() => setExecucoesDe(null)}
				automacao={execucoesDe}
				allHosts={allHosts}
			/>
		</Paper>
	);
}
